import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { CourseHero } from '../components/course/CourseHero';
import { TheaterPlayer } from '../components/course/TheaterPlayer';
import { QuizPanel } from '../components/course/QuizPanel';
import { CourseContents } from '../components/course/CourseContents';
import { ArrowLeft, Lock, Award, Trash2, ListVideo, Download, Clock, Video, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseDetail() {
  const { courseId } = useParams();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [curriculumData, setCurriculumData] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  const [currentLesson, setCurrentLesson] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contentsOpen, setContentsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Scroll anchor — on mobile the contents rail sits below the player, so
  // picking a lesson smooth-scrolls the theater back into view.
  const playerRef = useRef(null);

  useEffect(() => {
    if (!playerRef.current) return;
    if (!currentLesson?.id && !(showQuiz && currentQuiz)) return;
    const rect = playerRef.current.getBoundingClientRect();
    const alreadyVisible = rect.top >= 0 && rect.top < window.innerHeight * 0.4;
    if (alreadyVisible) return;
    playerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentLesson?.id, showQuiz, currentQuiz?.id]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user && courseId) {
      const params = new URLSearchParams(location.search);
      if (params.get('purchase') === 'success' && params.get('session_id')) {
        (async () => {
          try {
            await axios.post(
              `${API_URL}/api/courses/${courseId}/verify-purchase`,
              { session_id: params.get('session_id') },
              { headers: getAuthHeader() }
            );
            toast.success('Course unlocked — enjoy!');
          } catch (err) {
            console.error('Course purchase verification failed:', err);
            toast.error(err.response?.data?.detail || 'Could not confirm payment. Contact support if you were charged.');
          } finally {
            navigate(location.pathname, { replace: true });
            checkAccessAndLoad();
          }
        })();
      } else {
        checkAccessAndLoad();
      }
    }
  }, [user, authLoading, courseId]);

  // Keep the active lesson's completion flag in sync after a reload.
  useEffect(() => {
    if (!curriculumData || !currentLesson) return;
    const fresh = (curriculumData.curriculum || [])
      .flatMap((m) => m.lessons)
      .find((l) => l.id === currentLesson.id);
    if (fresh && fresh.is_completed !== currentLesson.is_completed) {
      setCurrentLesson((prev) => ({ ...prev, is_completed: fresh.is_completed }));
    }
  }, [curriculumData]);

  const checkAccessAndLoad = async (autoStart = true) => {
    try {
      const headers = getAuthHeader();

      const accessRes = await axios.get(`${API_URL}/api/courses/${courseId}/access`, { headers });
      setHasAccess(accessRes.data.has_access);

      if (!accessRes.data.has_access && accessRes.data.reason === 'payment_required') {
        const courseRes = await axios.get(`${API_URL}/api/courses/${courseId}`, { headers });
        setCurriculumData({ course: courseRes.data, curriculum: [], stats: {} });
        setLoading(false);
        return;
      }

      const currRes = await axios.get(`${API_URL}/api/courses/${courseId}/curriculum`, { headers });
      setCurriculumData(currRes.data);
      if (autoStart) autoStartLesson(currRes.data.curriculum || []);

      const downloadsRes = await axios.get(`${API_URL}/api/courses/${courseId}/downloads`, { headers });
      setDownloads(downloadsRes.data);
    } catch (error) {
      console.error('Failed to load course:', error);
      toast.error('Failed to load course');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  // LinkedIn-Learning behavior: the player is front and center and starts
  // where the learner left off. Deep-link hashes (#module-<id>) still win.
  const autoStartLesson = (curriculum) => {
    const flat = curriculum.flatMap((m) => m.lessons);
    if (!flat.length) return;
    const hash = location.hash || '';
    const modMatch = hash.match(/^#module-([\w-]+)$/);
    let target = null;
    let moduleId = null;
    if (modMatch) {
      const mod = curriculum.find((m) => m.id === modMatch[1]);
      if (mod?.lessons?.length) {
        target = mod.lessons.find((l) => l.content_type === 'video') || mod.lessons[0];
        moduleId = mod.id;
      }
    }
    if (!target) {
      target =
        flat.find((l) => !l.is_completed && l.content_type !== 'quiz') ||
        flat.find((l) => !l.is_completed) ||
        flat[0];
    }
    if (!moduleId) {
      moduleId = curriculum.find((m) => m.lessons.some((l) => l.id === target.id))?.id;
    }
    if (moduleId) setExpandedModules({ [moduleId]: true });
    startLesson(target);
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const shareModule = async (moduleId, moduleTitle) => {
    const url = `${window.location.origin}/courses/${courseId}#module-${moduleId}`;
    const shareData = {
      title: moduleTitle,
      text: `Check out the "${moduleTitle}" module on WeMasterminds`,
      url,
    };
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      /* fall through to clipboard */
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Module link copied — share away.');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const startLesson = async (lesson) => {
    if (!lesson) return;
    // Quiz-only lesson — skip the player and go straight to the quiz.
    if (lesson.content_type === 'quiz') {
      try {
        const headers = getAuthHeader();
        const quizRes = await axios.get(`${API_URL}/api/lessons/${lesson.id}/quiz`, { headers });
        setCurrentLesson(lesson);
        setCurrentQuiz(quizRes.data);
        setShowQuiz(true);
      } catch (error) {
        toast.error('This lesson has no quiz configured yet.');
      }
      return;
    }
    if (lesson.video_url || lesson.text_content || lesson.pdf_url) {
      setCurrentLesson(lesson);
      setShowQuiz(false);
      return;
    }
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API_URL}/api/lessons/${lesson.id}`, { headers });
      setCurrentLesson({ ...lesson, ...res.data });
      setShowQuiz(false);
    } catch (error) {
      toast.error('Failed to load lesson content');
    }
  };

  // Selecting from the contents rail: expand that module + play in place.
  const selectLesson = (lesson) => {
    const mod = (curriculumData?.curriculum || []).find((m) =>
      m.lessons.some((l) => l.id === lesson.id)
    );
    if (mod) setExpandedModules((prev) => ({ ...prev, [mod.id]: true }));
    startLesson(lesson);
  };

  const completeLesson = async (lesson) => {
    try {
      const headers = getAuthHeader();
      await axios.post(`${API_URL}/api/lessons/${lesson.id}/complete`, {}, { headers });
      toast.success('Lesson completed!');
      // Auto-advance: figure out the next lesson BEFORE the reload swaps state.
      const idx = flatLessons.findIndex((l) => l.id === lesson.id);
      const next = idx >= 0 ? flatLessons[idx + 1] : null;
      await checkAccessAndLoad(false);
      if (next) selectLesson(next);
    } catch (error) {
      toast.error('Failed to mark lesson complete');
    }
  };

  const startQuiz = async (quizId) => {
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API_URL}/api/quizzes/${quizId}`, { headers });
      setCurrentQuiz(res.data);
      setShowQuiz(true);
    } catch (error) {
      toast.error('Failed to load quiz');
    }
  };

  const handlePurchase = async () => {
    try {
      const headers = getAuthHeader();
      const res = await axios.post(`${API_URL}/api/courses/${courseId}/purchase`, {}, { headers });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success(res.data.message);
        checkAccessAndLoad();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase course');
    }
  };

  const handleDeleteCourse = async () => {
    setIsDeleting(true);
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API_URL}/api/courses/${courseId}`, { headers });
      toast.success('Course moved to trash. You have 30 days to restore.');
      navigate('/courses');
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to delete course';
      toast.error(detail);
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!curriculumData) return null;

  const { course, curriculum, stats, certificate } = curriculumData;
  const canEdit = course?.creator_id === user?.id || user?.is_admin;
  const flatLessons = (curriculum || []).flatMap((m) => m.lessons);
  const currentIdx = flatLessons.findIndex((l) => l.id === currentLesson?.id);
  const goToOffset = (off) => {
    const l = flatLessons[currentIdx + off];
    if (l) selectLesson(l);
  };

  return (
    <AppShell active="courses" title={course?.title || 'Course'}>
      <div className="min-h-full">
        {/* Delete-course confirmation dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={(open) => !isDeleting && setShowDeleteDialog(open)}>
          <AlertDialogContent data-testid="course-detail-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Move &ldquo;{course?.title}&rdquo; to trash?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>The course will be hidden from every listing and lesson page.</p>
                  <p className="text-muted-foreground">
                    You have <strong className="text-amber-400">30 days</strong> to restore it from the{' '}
                    <strong>Trash</strong> tab on the Courses page before it&apos;s permanently deleted.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting} data-testid="course-detail-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCourse}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="course-detail-delete-confirm"
              >
                {isDeleting ? 'Moving…' : 'Move to trash'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* No Access — cinematic hero + payment gate */}
        {!hasAccess && (
          <>
            <CourseHero
              course={course}
              stats={stats}
              curriculum={curriculum}
              hasAccess={false}
              canEdit={false}
              courseId={courseId}
              onPurchase={handlePurchase}
            />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <div className="bg-brand-surface border border-white/5 rounded-2xl p-10 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-brand-gold" />
                </div>
                <h2 className="text-2xl font-playfair text-white mb-2">Unlock This Masterclass</h2>
                <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
                  {course?.price > 0
                    ? `Purchase this course for $${course.price} or upgrade to Growth/Founder for free access.`
                    : 'Upgrade your membership to access this course.'}
                </p>
                {course?.price > 0 && (
                  <Button onClick={handlePurchase} className="bg-brand-gold text-black hover:brightness-110 px-8 py-3 font-semibold" data-testid="purchase-btn">
                    Purchase for ${course.price}
                  </Button>
                )}
                <Link to="/pricing" className="block mt-4">
                  <Button variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5">
                    View Membership Plans
                  </Button>
                </Link>
              </div>
            </div>
          </>
        )}

        {/* Has Access — LinkedIn-Learning-style theater layout */}
        {hasAccess && (
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
            {/* Compact header — the video is the star, not the banner */}
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap" data-testid="course-compact-header">
              <div className="flex items-center gap-3 min-w-0">
                <Link
                  to="/courses"
                  className="p-2 rounded-full hover:bg-white/5 text-gray-400 hover:text-white transition-colors flex-shrink-0"
                  data-testid="back-to-courses"
                  aria-label="Back to courses"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="min-w-0">
                  <h1 className="text-xl md:text-2xl font-playfair text-white truncate" data-testid="course-title">
                    {course?.title}
                  </h1>
                  <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
                    <span className="text-brand-gold uppercase tracking-wider">{course?.category || 'Masterclass'}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-600" />
                    <span>{stats?.total_lessons || 0} lessons</span>
                    <span className="w-1 h-1 rounded-full bg-gray-600" />
                    <span data-testid="header-progress">{Math.round(stats?.progress_percent || 0)}% complete</span>
                    {certificate && (
                      <span className="text-emerald-400 inline-flex items-center gap-1">
                        <Award className="w-3 h-3" /> Certified
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!contentsOpen && (
                  <Button
                    variant="outline"
                    onClick={() => setContentsOpen(true)}
                    className="border-white/10 text-white hover:bg-white/5 text-xs"
                    data-testid="contents-open-btn"
                  >
                    <ListVideo className="w-4 h-4 mr-1.5" /> Course contents
                  </Button>
                )}
                {canEdit && (
                  <>
                    <Link to={`/courses/${courseId}/edit`}>
                      <Button variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="edit-course-btn">
                        Edit
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteDialog(true)}
                      disabled={isDeleting}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 px-3 text-xs"
                      data-testid="delete-course-btn"
                      aria-label="Move course to trash"
                      title="Move to trash"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 items-start">
              {/* Main column — theater player + tabs */}
              <div className="flex-1 min-w-0 w-full">
                <div ref={playerRef} style={{ scrollMarginTop: '96px' }} data-testid="lesson-player-anchor">
                  {showQuiz && currentQuiz ? (
                    <QuizPanel
                      quiz={currentQuiz}
                      onSubmit={async (answers) => {
                        try {
                          const headers = getAuthHeader();
                          const res = await axios.post(
                            `${API_URL}/api/quizzes/${currentQuiz.id}/submit`,
                            { answers },
                            { headers }
                          );
                          if (res.data.passed) {
                            toast.success('Quiz passed! Lesson unlocked.');
                            checkAccessAndLoad(false);
                          } else {
                            toast.error(`Score ${Math.round(res.data.score)}% — need ${res.data.passing_score}% to pass. Try again!`);
                          }
                          return res.data;
                        } catch (error) {
                          toast.error('Failed to submit quiz');
                          return { score: 0, passed: false, correct: 0, total: currentQuiz.questions.length, passing_score: currentQuiz.passing_score || 80 };
                        }
                      }}
                      onClose={() => setShowQuiz(false)}
                    />
                  ) : (
                    <TheaterPlayer
                      lesson={currentLesson}
                      onComplete={completeLesson}
                      onStartQuiz={startQuiz}
                      onPrev={() => goToOffset(-1)}
                      onNext={() => goToOffset(1)}
                      hasPrev={currentIdx > 0}
                      hasNext={currentIdx >= 0 && currentIdx < flatLessons.length - 1}
                    />
                  )}
                </div>

                {/* Tabs under the player */}
                <div className="mt-6">
                  <div className="flex gap-1 border-b border-white/10" data-testid="course-tabs-bar">
                    {[
                      { id: 'overview', label: 'Overview' },
                      { id: 'downloads', label: `Downloads${downloads.length ? ` (${downloads.length})` : ''}` },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                          activeTab === tab.id
                            ? 'border-brand-gold text-brand-gold'
                            : 'border-transparent text-gray-400 hover:text-white'
                        }`}
                        data-testid={`tab-${tab.id}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {activeTab === 'overview' && (
                    <div className="py-6 space-y-6" data-testid="overview-panel">
                      {certificate && (
                        <div className="p-4 bg-green-500/15 border border-green-500/30 rounded-xl flex items-center gap-3">
                          <Award className="w-6 h-6 text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-green-400">Course Completed!</p>
                            <p className="text-xs text-green-300">Certificate ID: {certificate.id}</p>
                          </div>
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">About this course</h3>
                        <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
                          {course?.description || 'No description provided.'}
                        </p>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" /> {stats?.total_duration || course?.total_duration_minutes || 0} min total
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Video className="w-4 h-4" /> {stats?.total_lessons || 0} lessons
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Users className="w-4 h-4" /> {course?.enrollment_count || 0} enrolled
                        </span>
                        {stats?.quizzes_taken > 0 && (
                          <span>Avg quiz score: {Math.round(stats.average_quiz_score)}%</span>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'downloads' && (
                    <div className="py-6" data-testid="downloads-panel">
                      {downloads.length > 0 ? (
                        <div className="space-y-3 max-w-xl">
                          {downloads.map((download) => (
                            <a
                              key={download.id}
                              href={download.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                              data-testid={`download-${download.id}`}
                            >
                              <Download className="w-4 h-4 text-brand-gold flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">{download.title}</p>
                                <p className="text-xs text-gray-400">{download.is_free ? 'Free Download' : 'Member Only'}</p>
                              </div>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No downloadable resources for this course.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right rail — course contents */}
              {contentsOpen && (
                <div className="w-full lg:w-[360px] lg:flex-shrink-0 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] flex">
                  <CourseContents
                    curriculum={curriculum}
                    stats={stats}
                    expandedModules={expandedModules}
                    onToggleModule={toggleModule}
                    onShareModule={shareModule}
                    onSelectLesson={selectLesson}
                    currentLessonId={showQuiz ? null : currentLesson?.id}
                    onClose={() => setContentsOpen(false)}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
